(() => {
  const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const filename = src => new URL(src, document.baseURI).pathname.split('/').pop();
  function initProduct(root) {
    if (root.dataset.loReady === 'true') return;
    root.dataset.loReady = 'true';
    const mainImage = root.querySelector('.lo-gallery-main');
    const stage = root.querySelector('.lo-gallery-stage');
    const groups = [...root.querySelectorAll('.lo-option-group')];
    const colorGroup = groups.find(group => /colou?r/i.test(group.querySelector('legend span')?.textContent || ''));
    const colorPosition = colorGroup?.dataset.optionPosition;
    const selected = {};
    let variants = [];
    try { variants = JSON.parse(root.querySelector('[data-lo-variants]')?.textContent || '[]'); } catch {}
    const idInput = root.querySelector('[data-lo-variant-id]');
    const add = root.querySelector('[data-lo-add]');
    const addLabel = root.querySelector('[data-lo-add-label]');
    const mobileButton = root.querySelector('[data-lo-mobile-button]');
    const express = root.querySelector('[data-lo-express-checkout]');
    const form = root.querySelector('.lo-product-form');
    const cache = new Map();
    let imageTicket = 0;
    const hero = root.dataset.loHero || mainImage?.getAttribute('src');
    function imageInfo(src) {
      const base = new URL(src, document.baseURI);
      const slot = Math.ceil(mainImage?.getBoundingClientRect().width || Math.min(window.innerWidth, 800));
      const candidates = [480, 720, 960, 1200, 1400].map(width => {
        const url = new URL(base.href);
        url.searchParams.set('width', width);
        return `${url.href} ${width}w`;
      }).join(', ');
      base.searchParams.set('width', 1400);
      return { src: base.href, srcset: candidates, sizes: `${slot}px`, key: `${filename(src)}:${slot}:${window.devicePixelRatio || 1}` };
    }
    function prepareImage(src, priority = 'low') {
      const info = imageInfo(src);
      if (cache.has(info.key)) {
        const entry = cache.get(info.key);
        if (priority === 'high') entry.image.fetchPriority = 'high';
        return entry.ready;
      }
      const image = new Image();
      image.decoding = 'async';
      image.fetchPriority = priority;
      const ready = new Promise((resolve, reject) => {
        image.onload = () => Promise.resolve(image.decode?.()).catch(() => {}).then(() => resolve(info));
        image.onerror = () => { cache.delete(info.key); reject(new Error('Image could not load')); };
        image.sizes = info.sizes;
        image.srcset = info.srcset;
        image.src = info.src;
      });
      cache.set(info.key, { image, ready });
      return ready;
    }
    function setGalleryImage(src, alt) {
      if (!mainImage || !src) return;
      const ticket = ++imageTicket;
      root.querySelectorAll('.lo-thumb').forEach(button => {
        button.classList.toggle('is-active', filename(button.dataset.imageSrc) === filename(src));
      });
      if (filename(mainImage.src) === filename(src)) {
        if (alt) mainImage.alt = alt;
        stage?.classList.remove('lo-image-loading');
        return;
      }
      stage?.classList.add('lo-image-loading');
      prepareImage(src, 'high').then(info => {
        if (ticket !== imageTicket) return;
        stage?.querySelectorAll('.lo-gallery-transition-layer').forEach(layer => layer.remove());
        let outgoing;
        if (!reducedMotion() && mainImage.animate && stage) {
          outgoing = mainImage.cloneNode(false);
          outgoing.className = 'lo-gallery-transition-layer';
          outgoing.alt = '';
          outgoing.setAttribute('aria-hidden', 'true');
          stage.append(outgoing);
        }
        mainImage.sizes = info.sizes;
        mainImage.srcset = info.srcset;
        mainImage.src = info.src;
        if (alt) mainImage.alt = alt;
        stage?.classList.remove('lo-image-loading');
        if (outgoing) {
          outgoing.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 140, easing: 'linear', fill: 'forwards' }).finished.catch(() => {}).finally(() => outgoing.remove());
          mainImage.animate([{ transform: 'scale(1.012)' }, { transform: 'scale(1)' }], { duration: 180, easing: 'cubic-bezier(.2,.8,.2,1)' });
        }
      }).catch(() => {
        if (ticket !== imageTicket) return;
        mainImage.sizes = imageInfo(src).sizes;
        mainImage.srcset = imageInfo(src).srcset;
        mainImage.src = imageInfo(src).src;
        stage?.classList.remove('lo-image-loading');
      });
    }
    function money(cents) {
      const currency = window.Shopify?.currency?.active || 'USD';
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(cents) / 100);
    }
    function applySelection() {
      const matches = variant => Object.keys(selected).every(position => variant[`option${position}`] === selected[position]);
      const complete = groups.every(group => selected[group.dataset.optionPosition]);
      const variant = complete ? variants.find(matches) : undefined;
      const preview = colorPosition && selected[colorPosition] ? variants.find(matches) : undefined;
      groups.forEach(group => {
        const position = group.dataset.optionPosition;
        group.querySelectorAll('.lo-option').forEach(button => {
          const possible = variants.some(candidate => candidate[`option${position}`] === button.dataset.optionValue && Object.keys(selected).filter(key => key !== position).every(key => candidate[`option${key}`] === selected[key]) && candidate.available);
          button.classList.toggle('is-unavailable', !possible);
          button.classList.toggle('is-selected', selected[position] === button.dataset.optionValue);
          button.setAttribute('aria-pressed', String(selected[position] === button.dataset.optionValue));
          button.setAttribute('aria-disabled', String(!possible));
        });
        const label = group.querySelector('[data-selected-label]');
        if (label) label.textContent = selected[position] || 'Select';
      });
      const available = Boolean(variant?.available);
      if (idInput) { idInput.value = variant ? String(variant.id) : ''; idInput.disabled = !available; }
      if (add) add.disabled = !available;
      if (mobileButton) mobileButton.disabled = !available;
      if (express) express.hidden = !available;
      const missing = groups.filter(group => !selected[group.dataset.optionPosition]).map(group => group.querySelector('legend span')?.textContent.toLowerCase().replace('color', 'colour'));
      const label = variant ? (available ? 'Add to cart' : 'Sold out') : (missing.length ? `Choose ${missing.join(' and ')}` : 'Choose available options');
      if (addLabel) addLabel.textContent = label;
      if (mobileButton) mobileButton.textContent = available ? 'Add to cart ↗' : label;
      if (variant) root.querySelectorAll('[data-lo-price], [data-lo-mobile-price]').forEach(node => { node.textContent = money(variant.price); });
      const photo = variant || preview;
      if (photo?.featured_image?.src) setGalleryImage(photo.featured_image.src, photo.name || photo.title);
    }
    root.querySelectorAll('.lo-option').forEach(button => {
      button.addEventListener('click', () => { selected[button.dataset.optionPosition] = button.dataset.optionValue; applySelection(); });
      const warm = () => {
        if (button.dataset.optionPosition !== colorPosition) return;
        const candidate = variants.find(variant => variant[`option${colorPosition}`] === button.dataset.optionValue && variant.featured_image?.src);
        if (candidate) prepareImage(candidate.featured_image.src, 'high').catch(() => {});
      };
      button.addEventListener('pointerenter', warm, { passive: true });
      button.addEventListener('focus', warm);
      button.addEventListener('pointerdown', warm, { passive: true });
    });
    root.querySelectorAll('.lo-thumb').forEach(button => {
      button.addEventListener('click', () => setGalleryImage(button.dataset.imageSrc));
      button.addEventListener('pointerenter', () => prepareImage(button.dataset.imageSrc).catch(() => {}), { passive: true });
    });
    form?.addEventListener('submit', event => { if (!idInput?.value || idInput.disabled) { event.preventDefault(); form.querySelector('.lo-option')?.focus(); } });
    form?.addEventListener('reset', () => { Object.keys(selected).forEach(key => delete selected[key]); applySelection(); setGalleryImage(hero); });
    mobileButton?.addEventListener('click', () => { if (add && !add.disabled) add.click(); });
    applySelection();
    const connection = navigator.connection;
    if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return;
    const sources = [...new Set(variants.map(variant => variant.featured_image?.src).filter(Boolean))];
    const warmQueue = async () => { while (sources.length && root.isConnected) { const src = sources.shift(); try { await prepareImage(src); } catch {} } };
    const prewarm = () => { warmQueue(); warmQueue(); };
    if ('requestIdleCallback' in window) window.requestIdleCallback(prewarm, { timeout: 1200 });
    else window.setTimeout(prewarm, 200);
  }
  function initMotion(scope) {
    const targets = scope.querySelectorAll('.lo-story-copy, .lo-story-image, .lo-faq-grid > div, .lo-motion-copy, .lo-motion-frame, .lo-reviews-head, .lo-review-card');
    if (reducedMotion() || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        entry.target.classList.remove('lo-reveal-pending');
        entry.target.animate?.([{ opacity: .35, transform: 'translateY(14px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 320, delay: Number(entry.target.dataset.loDelay || 0), easing: 'cubic-bezier(.16,1,.3,1)' });
      });
    }, { rootMargin: '0px 0px 70px 0px', threshold: .08 });
    targets.forEach((target, index) => {
      if (target.dataset.loMotionReady) return;
      target.dataset.loMotionReady = 'true';
      if (target.getBoundingClientRect().top < window.innerHeight) return;
      target.dataset.loDelay = String((index % 3) * 30);
      observer.observe(target);
    });
    scope.querySelectorAll('.lo-details details').forEach(details => {
      if (details.dataset.loMotionReady) return;
      details.dataset.loMotionReady = 'true';
      details.addEventListener('toggle', () => {
        if (details.open && !reducedMotion()) details.querySelector('div')?.animate?.([{ opacity: .5, transform: 'translateY(-4px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 160, easing: 'ease-out' });
      });
    });
  }
  function init(scope = document) {
    scope.querySelectorAll('[data-lo-store]').forEach(initProduct);
    initMotion(scope);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
